/**
 * ProjectList page - Display all projects in a grid layout
 * Allows users to view and navigate to their projects
 */

import { Plus, FolderOpen } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProjectCard } from '@/components/project/ProjectCard';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks';
import { useProjects } from '@/hooks/useProjects';

// ============================================================================
// SKELETON COMPONENTS
// ============================================================================

function ProjectCardSkeleton() {
  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="h-6 w-3/4 bg-surface-subtle rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-surface-subtle rounded animate-pulse" />
          </div>
        </div>
        <div className="h-3 w-full bg-surface-subtle rounded animate-pulse" />
        <div className="flex gap-6 pt-2 border-t border-border-subtle">
          <div className="h-3 w-20 bg-surface-subtle rounded animate-pulse" />
          <div className="h-3 w-24 bg-surface-subtle rounded animate-pulse" />
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

function EmptyProjectsState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <Card>
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-subtle mb-6">
          <FolderOpen className="h-8 w-8 text-muted" />
        </div>
        <h3 className="text-xl font-semibold text-primary mb-2">
          No projects yet
        </h3>
        <p className="text-sm text-secondary mb-8 max-w-md mx-auto">
          Create your first project to start analyzing video creatives and making
          data-driven decisions about your ad campaigns.
        </p>
        <Button
          onClick={onCreateClick}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Create First Project
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProjectList() {
  const navigate = useNavigate();
  const { currentTeam } = useAuth();
  const { data: projects, isLoading } = useProjects(currentTeam?.teamId);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter projects by search query
  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (!searchQuery.trim()) return projects;

    const query = searchQuery.toLowerCase();
    return projects.filter(
      (project) =>
        project.name.toLowerCase().includes(query) ||
        project.brandName?.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  const handleCreateProject = () => {
    navigate('/projects/new');
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const hasProjects = projects && projects.length > 0;
  const hasFilteredProjects = filteredProjects.length > 0;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">
            PROJECTS
          </div>
          <h1 className="text-4xl font-black text-primary leading-tight tracking-tight">
            All Projects
          </h1>
          {hasProjects && (
            <p className="text-sm text-secondary mt-2">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'} total
            </p>
          )}
        </div>
        {hasProjects && (
          <Button
            onClick={handleCreateProject}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New Project
          </Button>
        )}
      </div>

      {/* Search Bar (only show if there are projects) */}
      {hasProjects && (
        <div className="max-w-md">
          <Input
            variant="search"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
          <ProjectCardSkeleton />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasProjects && (
        <EmptyProjectsState onCreateClick={handleCreateProject} />
      )}

      {/* Projects Grid */}
      {!isLoading && hasProjects && (
        <>
          {hasFilteredProjects ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.projectId}
                  project={project}
                  onClick={() => handleProjectClick(project.projectId)}
                  showStatus={project.status !== 'active'}
                />
              ))}
            </div>
          ) : (
            <Card>
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-subtle mb-4">
                  <FolderOpen className="h-6 w-6 text-muted" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2">
                  No projects found
                </h3>
                <p className="text-sm text-secondary mb-6">
                  No projects match &quot;{searchQuery}&quot;. Try a different search term.
                </p>
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="secondary"
                >
                  Clear Search
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
